import * as React from "react";
import { FC, useEffect, useMemo, useCallback, useState } from "react";
import { connect, Dispatch } from "react-redux";
import { format } from "lib/globalize-util";
import { FormatMessage } from "react-globalize";
import type { IScope } from "reducers/scope";
import Grid from "@material-ui/core/Grid";
import theme from "styles/theme";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { MediaDialog } from "components/dialog/media-dialog";
import { IStore } from "reducers";
import { IItemRecord } from "records/item/item";
import NotificationError from "components/notification-banners";
import { Uploader } from "components/product-media/components/uploader";
import Reminders from "components/product-media/components/reminders";
import { MEDIA_TYPE_THREEDIUM, VIDEO_3D_IMAGE_SPECIFICATION_LINK } from "constants/media";
import { IAsset } from "constants/assets";
import { ContentLoading } from "components/content-loading";
import { IMediaAsset, IMediaAssetMap } from "constants/media-asset";
import {
    fetchMediaAssets,
    mutateMediaAssets,
    resetProductMediaAssets,
    resetProductMedia,
    upload3D,
} from "actions/product-media-actions";
import { createNewMediaKey, transformMediaMap } from "helpers/manage-media";
import { DATATEST } from "constants/integration";
import { Image3DIcon } from "../icons/image-3d";
import classNames from "classnames";

const MAX_SIZE = 40; // MB

interface IProps {
    product: IItemRecord;
    open: boolean;
    onClose(reload?: boolean): void;
}

interface IReduxProps {
    asset: IAsset;
    error: string | Error;
    isUploading: boolean;
    mediaAssetMap: IMediaAssetMap;
    revision: string;
    isUpdating: boolean;
    isFetching: boolean;
    scope: IScope;
}

interface IDispatchProps {
    resetProductMedia(): void;
    fetchProductMedia(id: string): void;
    updateMedia(productId: string, revision: string, mediaMap: IMediaAssetMap): void;
    onUpload(product: string, id: string): void;
    onReset(): void;
}

const Upload3DDialog: FC<IProps & IReduxProps & IDispatchProps> = (props) => {
    const { product, open, onClose, scope } = props;
    const { asset, error, onReset, mediaAssetMap, revision } = props;
    const { onUpload, isUploading, isUpdating, isFetching, fetchProductMedia, resetProductMedia, updateMedia } = props;

    const mediaMap = useMemo(() => transformMediaMap(mediaAssetMap), [mediaAssetMap]);

    const [directUploading, setDirectUploading] = useState<boolean>(false); // internal flag of uploader processing
    const [uploadError, setUploadError] = useState<string>(null);
    const [close, setClose] = useState<boolean>(false); // close the dialog right after update is finished

    const errorMessage = useMemo(
        () =>
            format(
                "There were issues with 3D image file you uploaded. Make sure it is the correct file type/size and only a single file at a time"
            ),
        []
    );

    useEffect(() => {
        fetchProductMedia(product._id);
    }, [product]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => setUploadError(error && errorMessage), [error, errorMessage]);

    const handleClose = useCallback(
        (reload?: boolean) => {
            onReset(); // reset uploading state
            resetProductMedia();
            setUploadError(null);
            onClose(reload);
        },
        [onClose, onReset, resetProductMedia]
    );

    const handleUpload = useCallback(
        (result: { id: string }) => {
            if (result) {
                onUpload(product._id, result.id);
            }
        },
        [product, onUpload]
    );

    const handleSave = useCallback(() => {
        if (asset) {
            const assetKey = createNewMediaKey(mediaMap);
            updateMedia(product._id, revision, {
                ...mediaMap,
                [assetKey]: { ...asset, type: MEDIA_TYPE_THREEDIUM } as IMediaAsset,
            });
            setClose(true);
        } else {
            handleClose();
        }
    }, [product, asset, revision, mediaMap, updateMedia, handleClose]);

    const handleCloseDialog = useCallback(() => handleClose(false), [handleClose]);

    const handleReject = useCallback(() => setUploadError(errorMessage), [errorMessage]);
    const handleUploadStart = useCallback(() => setUploadError(null), []);
    const handleHideError = useCallback(() => setUploadError(null), []);
    const handleError = useCallback(
        ({ message }: { message: string } | Error) => setUploadError(message || errorMessage),
        [errorMessage]
    );

    useEffect(() => {
        if (close && !isUpdating) {
            if (!error) {
                handleClose(true);
            } else {
                setClose(false);
            }
        }
    }, [close, isUpdating, handleClose]); // eslint-disable-line react-hooks/exhaustive-deps

    const classes = useStyles({});
    return (
        <MediaDialog
            open={open}
            title={format("Add 3D Image")}
            onClose={handleCloseDialog}
            onSave={handleSave}
            disabled={directUploading || isUploading || isUpdating || isFetching}
        >
            <div className={classes.contentTitle}>
                {format("Review the specifications and select your 3D image file to upload.")}
            </div>
            <NotificationError classes={{ root: classes.error }} message={uploadError} onClose={handleHideError} />
            <Grid container={true} wrap="wrap" justify="space-around" spacing={2}>
                <Grid className={classes.margin} item={true}>
                    <Uploader
                        inputProps={{
                            "data-test": DATATEST.MEDIA_DIALOG.THREEDIUM_UPLOADER,
                        }}
                        accept=".glb"
                        maxSize={MAX_SIZE * 1024 * 1024}
                        loading={directUploading || isUploading}
                        onUpload={handleUpload}
                        onUploadError={handleError}
                        onDropReject={handleReject}
                        onUploadStart={handleUploadStart}
                        onLoading={setDirectUploading}
                        scope={scope}
                    >
                        <div className={classes.dragContainer}>
                            {asset && <Image3DIcon className={classNames(classes.icon, classes.success)} />}
                            {!asset && <Image3DIcon className={classes.icon} />}
                            <div className={classes.dragLabel}>
                                <FormatMessage
                                    path="Drag your 3D Image file here |br| or |span|click here|span| to select"
                                    elements={{ br: <br />, span: <span className={classes.click} /> }}
                                />
                            </div>
                        </div>
                    </Uploader>
                </Grid>
                <Grid
                    className={classes.margin}
                    data-test={DATATEST.MEDIA_DIALOG.THREEDIUM_SPECIFICATIONS}
                    item={true}
                    xs={true}
                >
                    <Reminders title={format("3D Image specifications:")}>
                        <li>{format("GLB format only")}</li>
                        <li>{format("Texture size: 2K optimized (4K original)")}</li>
                        <li>{format("File size: |max| max", { max: `${MAX_SIZE} MB` })}</li>
                        <li>
                            <FormatMessage
                                path="For more details, see |a|3D Image Specifications|a|."
                                elements={{
                                    a: (
                                        <a
                                            data-test={DATATEST.MEDIA_DIALOG.THREEDIUM_SPECIFICATIONS_LINK}
                                            className={classes.link}
                                            href={VIDEO_3D_IMAGE_SPECIFICATION_LINK}
                                            target="_blank"
                                        />
                                    ),
                                }}
                            />
                        </li>
                    </Reminders>
                </Grid>
            </Grid>
            {isUpdating && <ContentLoading className={classes.loading} />}
        </MediaDialog>
    );
};

const useStyles = makeStyles({
    contentTitle: {
        fontSize: 14,
        padding: 10,
    },
    error: {
        margin: 10,
    },
    loading: {
        left: -24,
    },
    click: {
        textDecoration: "underline",
    },
    icon: {
        width: 160,
        height: 160,
        padding: 30,
        margin: 10,
        color: theme.palette.grey,
    },
    dragLabel: {
        fontSize: 14,
        textAlign: "center",
        lineHeight: "1.8em",
        color: theme.palette.darkGrey,
    },
    margin: {
        margin: 20,
        "& .uploaderContainer > div:first-child": {
            background: `${theme.palette.lightestGrey} !important`, // override drag over validation
        },
        ["@media(max-width: 600px)"]: {
            margin: 10,
        },
    },
    dragContainer: {
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
    },
    imagePreview: {
        display: "flex",
        justifyContent: "center",
        "& img": {
            width: "100%",
            alignSelf: "flex-start",
        },
    },
    success: {
        color: theme.palette.accent1Color,
    },
    link: {
        textDecoration: "underline",
        color: theme.palette.accent1Color,
        padding: 0,
    },
});

const mapStateToProps = (store: IStore): IReduxProps => ({
    asset: store.productMedia.payload,
    error: store.productMedia.error,
    isUploading: store.productMedia.isUploading,
    mediaAssetMap: store.productMediaAssets?.payload?.media as IMediaAssetMap,
    isFetching: store.productMediaAssets?.isFetching,
    isUpdating: store.productMediaAssets?.isUpdating,
    revision: store.productMediaAssets?.payload?.revision,
    scope: store.scope,
});

const mapDispatchToProps = (dispatch: Dispatch<{}>): IDispatchProps => ({
    fetchProductMedia: (productId: string) => dispatch(fetchMediaAssets(productId)),
    resetProductMedia: () => dispatch(resetProductMediaAssets()),
    updateMedia: (productId: string, revision: string, mediaMap: IMediaAssetMap) =>
        dispatch(mutateMediaAssets(productId, revision, mediaMap)),
    onUpload: (product: string, id: string) => dispatch(upload3D(product, id)),
    onReset: () => dispatch(resetProductMedia()),
});

export default connect<IReduxProps, IDispatchProps, IProps>(mapStateToProps, mapDispatchToProps)(Upload3DDialog);
